import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { avatarUrl } from "discourse/lib/avatar-utils";
import { prioritizeNameInUx } from "discourse/lib/settings";
import virtualElementFromPoint from "discourse/lib/virtual-element-from-point";
import { eq } from "discourse/truth-helpers";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import {
  toggleTileFullscreen,
  trackFullscreen,
} from "../../lib/live/fullscreen.js";
import {
  DEFAULT_TILE_ASPECT,
  trackVideoAspect,
} from "../../lib/live/video-grid-layout.js";
import LiveParticipantSidebarContextMenu from "../live-participant-sidebar-context-menu.gjs";

export default class LiveVideoTile extends Component {
  @service menu;
  @service liveRooms;
  @service liveWebrtc;

  @tracked aspect = null;
  @tracked isFullscreen = false;

  trackVideoAspect = trackVideoAspect;
  trackFullscreen = trackFullscreen;

  get participant() {
    return this.args.participant;
  }

  get isSpeaking() {
    return this.liveRooms.isParticipantSpeaking(
      this.args.room?.id,
      this.participant.id
    );
  }

  get tileStyle() {
    return trustHTML(`aspect-ratio: ${this.aspect ?? DEFAULT_TILE_ASPECT};`);
  }

  // The menu only offers audio controls for a call the user is in, so
  // page visitors who haven't joined the room don't get one.
  get canShowMenu() {
    return (
      this.liveWebrtc.connectionStateFor(this.args.room?.id) === "connected"
    );
  }

  get fullscreenTitle() {
    return this.isFullscreen
      ? i18n("live.video.exit_fullscreen")
      : i18n("live.video.fullscreen");
  }

  get stream() {
    if (this.args.isSelf) {
      return this.liveWebrtc.localVideoStream;
    }

    return this.liveWebrtc.remoteStreamFor(
      this.args.room.id,
      this.participant.id
    );
  }

  get publishingKind() {
    if (this.args.isSelf) {
      return this.liveWebrtc.localVideoKind;
    }

    if (this.participant.is_screen_sharing) {
      return "screen";
    }

    if (this.participant.is_video_on) {
      return "camera";
    }

    return null;
  }

  get showVideo() {
    return this.args.showVideo && !!this.publishingKind && !!this.stream;
  }

  get avatarSrc() {
    return avatarUrl(this.participant.avatar_template, "huge");
  }

  get displayName() {
    return prioritizeNameInUx(this.participant.name)
      ? this.participant.name
      : this.participant.username;
  }

  @action
  handleAspect(aspect) {
    this.aspect = aspect;
    this.args.onAspect?.(this.participant.id, aspect);
  }

  @action
  setFullscreen(isFullscreen) {
    this.isFullscreen = isFullscreen;
  }

  @action
  toggleFullscreen(event) {
    toggleTileFullscreen(event.currentTarget.closest(".live-video-tile"));
  }

  @action
  openContextMenu(event) {
    if (!this.canShowMenu) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.#showParticipantMenu(
      virtualElementFromPoint(event.clientX, event.clientY),
      "bottom-start"
    );
  }

  @action
  openTileMenu(event) {
    event.stopPropagation();
    this.#showParticipantMenu(event.currentTarget, "top-end");
  }

  #showParticipantMenu(anchor, placement) {
    this.menu.show(anchor, {
      identifier: "live-participant-menu",
      component: LiveParticipantSidebarContextMenu,
      placement,
      modalForMobile: true,
      data: {
        room: this.args.room,
        participant: this.participant,
        canManageRoom: this.args.room?.can_manage,
        isCurrentUser: this.args.isSelf,
        isSpotlighted: this.args.spotlighted,
        onSpotlight: this.args.onSpotlight,
      },
    });
  }

  <template>
    <div
      class={{dConcatClass
        "live-video-tile"
        (if this.showVideo "--video" "--avatar")
        (if (eq this.publishingKind "screen") "--screen")
        (if @isSelf "--self")
        (if this.isSpeaking "--speaking")
      }}
      data-user-id={{this.participant.id}}
      style={{this.tileStyle}}
      {{this.trackFullscreen this.setFullscreen}}
      {{on "contextmenu" this.openContextMenu}}
    >
      {{#if this.showVideo}}
        <video
          autoplay
          class="live-video-tile__video"
          muted
          playsinline
          {{didInsert (fn this.liveWebrtc.attachVideoStream this.stream)}}
          {{didUpdate
            (fn this.liveWebrtc.attachVideoStream this.stream)
            this.stream
          }}
          {{this.trackVideoAspect this.handleAspect}}
        ></video>
      {{else}}
        <div class="live-video-tile__avatar">
          <img alt={{this.displayName}} src={{this.avatarSrc}} />
        </div>
      {{/if}}

      <div class="live-video-tile__info">
        <span class="live-video-tile__name">{{this.displayName}}</span>
        {{#if this.participant.is_muted}}
          {{dIcon "microphone-slash"}}
        {{/if}}
        {{#if this.participant.is_deafened}}
          {{dIcon "volume-xmark"}}
        {{/if}}
        {{#if (eq this.publishingKind "screen")}}
          {{dIcon "display"}}
        {{/if}}
      </div>

      {{#if this.canShowMenu}}
        <button
          aria-label={{i18n "live.participant.menu_title"}}
          class="btn btn-icon no-text live-video-tile__menu"
          title={{i18n "live.participant.menu_title"}}
          type="button"
          {{on "click" this.openTileMenu}}
        >
          {{dIcon "ellipsis-vertical"}}
        </button>
      {{/if}}

      {{#if this.showVideo}}
        <button
          aria-label={{this.fullscreenTitle}}
          class="btn btn-icon no-text live-video-tile__fullscreen"
          title={{this.fullscreenTitle}}
          type="button"
          {{on "click" this.toggleFullscreen}}
        >
          {{dIcon (if this.isFullscreen "compress" "expand")}}
        </button>
      {{/if}}
    </div>
  </template>
}
